import { Link } from '@tanstack/react-router';
import { CircleUserRound, LogOut, type LucideProps, UserCog, Wrench } from 'lucide-react';
import type React from 'react';
import { useTranslation } from 'react-i18next';

import { useEffect, useRef } from 'react';
import { useBreakpoints } from '~/hooks/use-breakpoints';
import { useOnlineManager } from '~/hooks/use-online-manager';
import { AvatarWrap } from '~/modules/common/avatar-wrap';
import { MainFooter } from '~/modules/common/main-footer';
import { toaster } from '~/modules/common/toaster';
import { buttonVariants } from '~/modules/ui/button';
import { ScrollArea } from '~/modules/ui/scroll-area';
import { useUserStore } from '~/store/user';
import { cn } from '~/utils/cn';
import { numberToColorClass } from '~/utils/number-to-color-class';

type AccountButtonProps = {
  lucide: React.ElementType<LucideProps>;
  label: string;
  id: string;
  action: string;
} & ({ offlineAccess: false; isOnline: boolean } | { offlineAccess: true; isOnline?: never });

// Create a button for each account action
const AccountButton = ({ offlineAccess, isOnline, lucide: Icon, label, id, action }: AccountButtonProps) => {
  const { t } = useTranslation();

  const isDisabled = offlineAccess ? false : !isOnline;
  return (
    <Link
      disabled={isDisabled}
      onClick={() => {
        if (isDisabled) toaster(t('common:action.offline.text'), 'warning');
      }}
      data-sign-out={id === 'btn-signout'}
      id={id}
      to={action}
      className={cn(
        buttonVariants({ variant: 'ghost', size: 'lg' }),
        'data-[sign-out=true]:text-red-600 hover:bg-accent/50 w-full justify-start text-left outline-hidden sm:focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      )}
    >
      <Icon className="mr-2 h-4 w-4" aria-hidden="true" />
      {label}
    </Link>
  );
};

export const AccountSheet = () => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const isMobile = useBreakpoints('max', 'sm');
  const { isOnline } = useOnlineManager();

  const isSystemAdmin = user.role === 'admin';
  const buttonWrapper = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isMobile) return;
    const firstRow = buttonWrapper.current?.querySelector<HTMLElement>('#btn-profile');
    firstRow?.focus();
  }, []);

  return (
    <ScrollArea className="h-full" id="nav-sheet">
      <div ref={buttonWrapper} className="p-3 flex flex-col gap-4 min-h-[calc(100vh-0.5rem)]">
        <Link to="/users/$idOrSlug" params={{ idOrSlug: user.slug }} className="w-full relative">
          <div
            className={`relative transition-all duration-300 hover:bg-opacity-50 hover:-mx-8 -mx-4 -mt-4 bg-cover bg-center h-24 bg-opacity-80 ${
              user.bannerUrl ? '' : numberToColorClass(user.id)
            }`}
            style={user.bannerUrl ? { backgroundImage: `url(${user.bannerUrl})` } : {}}
          >
            <AvatarWrap
              className="h-16 w-16 absolute top-4 text-2xl left-[50%] -ml-8 border-bg border-2 rounded-full"
              type="user"
              id={user.id}
              name={user.name}
              url={user.thumbnailUrl}
            />
          </div>
        </Link>

        <div className="flex flex-col gap-1 max-sm:mt-4">
          <AccountButton
            offlineAccess={false}
            isOnline={isOnline}
            lucide={CircleUserRound}
            id="btn-profile"
            label={t('common:view_item', { item: t('common:profile').toLowerCase() })}
            action={`/users/${user.slug}`}
          />
          <AccountButton
            offlineAccess={false}
            isOnline={isOnline}
            lucide={UserCog}
            id="btn-account"
            label={t('common:settings')}
            action="/settings"
          />
          {isSystemAdmin && (
            <AccountButton
              offlineAccess={false}
              isOnline={isOnline}
              lucide={Wrench}
              id="btn-system"
              label={t('common:system_panel')}
              action="/system/users"
            />
          )}
          <AccountButton offlineAccess={false} isOnline={isOnline} lucide={LogOut} id="btn-signout" label={t('common:sign_out')} action="/sign-out" />
        </div>

        <div className="grow border-b border-dashed" />

        <MainFooter className="items-center" />
      </div>
    </ScrollArea>
  );
};
